import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { hasLiveSession, intakeUrl, isVaultPath } from './frontDoor';
import { RouterContext, type RouterValue } from './context';
import { assignDocument, replaceDocument } from './documentNavigation';
import { getLocation, push, subscribe } from './history';
import { parseLocation, staticSurfaceFor } from './routes';

/** Duration of the page exit fade; matches `.page-content--exit` in the stylesheet. */
export const PAGE_EXIT_MS = 300;
/** Delay between the URL change and removing the exit class (production value). */
const PAGE_ENTER_MS = 50;

/**
 * The pathname this document was loaded at. If the SPA itself was served at a static surface's
 * canonical URL (the surface file is missing), replacing the location with that URL would reload
 * the same document forever; the hand-off is skipped and reported instead.
 */
const DOCUMENT_PATHNAME = window.location.pathname;

export function RouterProvider({ children }: { readonly children: ReactNode }) {
  const location = useSyncExternalStore(subscribe, getLocation);
  const [exiting, setExiting] = useState(false);
  const exitTimer = useRef<number | undefined>(undefined);
  const enterTimer = useRef<number | undefined>(undefined);

  // FN-D14: /shadow-oracle is a document of its own; the SPA entry is replaced by them.
  const surface = staticSurfaceFor(location.pathname);
  // 2026-09-17, on ☧ 0's instruction: the sovereign-stack page carries the consent tick and the
  // passwordless magic-link intake itself, so a signed-out visitor is no longer sent to
  // /go/sovereign-stack (removed 2026-09-26). Only static surfaces hand over now.
  const redirecting = surface !== null;

  // Static surfaces and the front door on hard load, push, replace and back/forward. The page
  // fallback is rendered meanwhile (`redirecting`), never the not-found view.
  useLayoutEffect(() => {
    if (surface !== null) {
      if (location.pathname === surface && DOCUMENT_PATHNAME === surface) {
        console.error(`[router] ${surface} was served by the SPA instead of its static surface; not reloading it.`);
        return;
      }
      replaceDocument(surface);
      return;
    }
    if (redirecting) replaceDocument(intakeUrl(location.search, window.location.hash));
  }, [surface, redirecting, location]);

  useEffect(
    () => () => {
      window.clearTimeout(exitTimer.current);
      window.clearTimeout(enterTimer.current);
    },
    [],
  );

  const navigate = useCallback((url: string) => {
    const target = new URL(url, window.location.href);
    if (target.origin !== window.location.origin) {
      assignDocument(target.href);
      return;
    }
    if (isVaultPath(target.pathname) && !hasLiveSession()) {
      assignDocument(intakeUrl(target.search, target.hash));
      return;
    }
    const next = target.pathname + target.search + target.hash;
    // FN-D14: the SPA entry is pushed and then replaced by the surface (history: origin page → surface).
    if (staticSurfaceFor(target.pathname) !== null) {
      window.clearTimeout(exitTimer.current);
      window.clearTimeout(enterTimer.current);
      setExiting(false);
      push(next);
      return;
    }
    // Other static surfaces (/go/, /academy/, /cart …) and unknown URLs are documents of their own.
    if (parseLocation(target.pathname).page === 'not-found') {
      assignDocument(target.href);
      return;
    }
    if (next === window.location.pathname + window.location.search + window.location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    window.clearTimeout(exitTimer.current);
    window.clearTimeout(enterTimer.current);
    setExiting(true);
    exitTimer.current = window.setTimeout(() => {
      push(next);
      window.scrollTo(0, 0);
      enterTimer.current = window.setTimeout(() => {
        setExiting(false);
      }, PAGE_ENTER_MS);
    }, PAGE_EXIT_MS);
  }, []);

  const value = useMemo<RouterValue>(
    () => ({
      route: location.route,
      pathname: location.pathname,
      search: location.search,
      exiting,
      redirecting,
      navigate,
    }),
    [location, exiting, redirecting, navigate],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}
